import { Icons } from '@commenda/kakashi';
import {
  TBaseHumanStepDefinition,
  TWorkflowExecution,
  WorkflowRuntimeStepStatus,
} from '@commenda/types';
import { cn, formatDuration } from '@commenda/utils';
import { useMemo } from 'react';



type TTaskSLABadgesProps = {
  workflowExecution?: TWorkflowExecution;
  workflowDefinitionStepId?: string;
}

export const TaskSLABadges = ({
  workflowExecution,
  workflowDefinitionStepId,
}: TTaskSLABadgesProps) => {
  const slaData = useMemo(() => {
    if (!workflowExecution || !workflowDefinitionStepId) return null;


    const definition = workflowExecution.WorkflowTemplateVersion?.definition;
    const stepExecutions = workflowExecution.runtime?.stepExecutions;

    if (!definition || !stepExecutions) return null;

    const stepDefinition = definition.steps.find(
      (step) => step.id === workflowDefinitionStepId
    ) as TBaseHumanStepDefinition | undefined;

    if (!stepDefinition?.estimatedTimeToComplete?.durationMs) {
      return null;
    }

    const stepExecution = Object.values(stepExecutions).find(
      (exec) => exec.stepDefinitionId === workflowDefinitionStepId
    );

    const slaMs = stepDefinition.estimatedTimeToComplete.durationMs;

    let actualMs = 0;
    if (stepExecution?.startedAt) {
      const startTime = new Date(stepExecution.startedAt).getTime();

      if (
        stepExecution.status === WorkflowRuntimeStepStatus.COMPLETED &&
        stepExecution.completedAt
      ) {
        const endTime = new Date(stepExecution.completedAt).getTime();
        actualMs = endTime - startTime;
      } else if (
        stepExecution.status !== WorkflowRuntimeStepStatus.DISABLED
      ) {
        actualMs = Date.now() - startTime;
      }
    }

    return {
      slaMs,
      actualMs,
      hasStarted: !!stepExecution?.startedAt,
      isOnTrack: actualMs <= slaMs,
    };
  }, [workflowExecution, workflowDefinitionStepId]);

  if (!slaData) return null;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-bg-secondary border border-border-primary">
        <Icons.Clock className="size-3 text-text-secondary" />
        <span className="txt-extra-small-medium text-text-secondary">
          SLA: {formatDuration(slaData.slaMs)}
        </span>
      </div>

      {slaData.hasStarted && (
        <div className={cn("flex items-center gap-1 px-2 py-1 rounded-full border border-border-success", slaData.isOnTrack ? 'bg-bg-success-subtle' : 'bg-bg-error-subtle')}>
          <Icons.ClockRefresh className={cn("size-3", slaData.isOnTrack ? 'text-text-success' : 'text-text-error')} />
          <span className={cn("txt-extra-small-medium", slaData.isOnTrack ? 'text-text-success' : 'text-text-error')}>
            Actual: {formatDuration(slaData.actualMs)}
          </span>
        </div>
      )}
    </div>
  );
};
